import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { Injectable } from '@angular/core';
import { Observable, of } from "rxjs";
import { tap } from 'rxjs/operators';

import { PermissionService } from '@core/services/permission.service';

import { environment } from '@env/environment';

import { SignUpTypeEnum } from '@shared/enums/sign-up-type.enum';
import { fullNamePropertyCreator } from '@shared/utils/object-mapper.util';

import { NgxPermissionsService } from 'ngx-permissions';
import ActionTypesEnum from "@shared/enums/action-constant.enum";
import { RegisterService } from "@core/services/register.service";
import { CustomEventService } from "@shared/services/custom-event.service";

const userUrl = `${environment.apiBaseUrl}platform/users`;
const userSettingsUrl = `${environment.apiBaseUrl}platform/user-settings`

const headers = new HttpHeaders({
  'x-no-notification': 'true'
});

@Injectable({
  providedIn: 'root'
})
export class UserService {

  activeUser: ActiveUser.ActiveUserModel;
  activeUserPermissions = [];
  sidebarInfo :any;
  cacheMenu: any;

  constructor(
    private http: HttpClient,
    private permissionService: PermissionService,
    private registerService: RegisterService,
    private customEventService: CustomEventService,
    private permissionsService: NgxPermissionsService
  ) { }

  getAuthenticatedUser(): Observable<ActiveUser.ActiveUserModel> {
    return this.http.get<ActiveUser.ActiveUserModel>(`${userUrl}/me`).pipe(
      tap(res => {
        const user = res as ActiveUser.ActiveUserModel;
        this.activeUser = user;
        this.activeUser = fullNamePropertyCreator(this.activeUser);
        user.roles.forEach(item => {
          this.activeUserPermissions = [
            ...this.activeUserPermissions,
            item.name,
            ...item.permissions
          ]
        });
        this.permissionService.setPermissions(user);

        this.permissionsService.flushPermissions();
        this.permissionsService.loadPermissions(this.activeUserPermissions);
      })
    );
  }
  changePassword(changePasswordForm: ActiveUser.ChangePasswordFormModel): Observable<any> {
    const { id, ...passwords} = changePasswordForm;

    return this.http.put<any>(`${userUrl}/${id}/change-password`, passwords);
  }

  createRoot(user: Session.LoginFormModel): Observable<any> {
    return this.http.post<any>(`${userUrl}/system`, user, { headers });
  }

  list(): Observable<User.UserModel[]> {
    return this.http.get<User.UserModel[]>(`${userUrl}`);
  }

  get(id: string): Observable<User.UserModel> {
    return this.http.get<User.UserModel>(`${userUrl}/${id}`);
  }

  create(user: User.UserModel): Observable<User.UserModel> {
    return this.http.post<User.UserModel>(`${userUrl}`, user);
  }

  update(user: User.UserModel): Observable<User.UserModel> {
    return this.http.put<User.UserModel>(`${userUrl}/${user.id}`, user);
  }

  updateAvatar(user: ActiveUser.Profile): Observable<any> {
    return this.http.put<any>(`${userUrl}/${user.id}/avatar`, user);
  }


  getSidebarInfo(userId,parameterKey):Observable<any> {
    let params = new HttpParams();

    if (parameterKey) {
      params = params.append('parameterKey', parameterKey);
    }

    if(this.cacheMenu) {
      return of(this.cacheMenu);
    } else {
      return this.http.get<any>(`${userSettingsUrl}/${userId}`, { params }).pipe(
        tap(menu => {
          this.cacheMenu = menu;
        })
      );

    }
  }

  updateSidebarInfo(userId,parameterKey, parameterValue): Observable<any> {
    return this.http.put<any>(`${userSettingsUrl}/update`, { userId,parameterKey, parameterValue })
  }

  delete(id: string): Observable<any> {
    return this.http.delete<any>(`${userUrl}/${id}`);
  }

  getFormFields(): Observable<User.UserFieldsModel> {
    return this.http.get<User.UserFieldsModel>(`${userUrl}/fields`);
  }

  getImportUserList(formData: any): Observable<any> {
    return this.http.post<any>(`${userUrl}/import`,formData, { headers });
  }

  updateUserStatus(userId: string, enabled: boolean, hideNotification?: boolean): Observable<any> {
    const status = enabled ? 'activate' : 'deactivate';

    let options = {};

    if (hideNotification) {
      options = { headers }
    }

    return this.http.put<any>(`${userUrl}/${userId}/${status}`, {}, options);
  }

  resetPassword(userId: string): Observable<any> {
    return this.http.put<any>(`${userUrl}/${userId}/reset-password`, {}, { headers });
  }

  unlockUser(userId: string): Observable<any> {
    return this.http.put<any>(`${userUrl}/${userId}/unlock`, {}, {headers});
  }

  tutorialMode(id: string, isTutorialMode: boolean): Observable<any> {
    return this.http.put<any>(`${userUrl}/${id}/tutorial-mode/${isTutorialMode}`, {});
  }

  getLimitations(): Observable<User.Limitations> {
    return this.http.get<User.Limitations>(`${userUrl}/limitations`);
  }

  createUserPlayground(user: User.PlaygroundRegisterModel): Observable<any> {
    return this.http.post<any>(`${userUrl}/playground`, user, {headers});
  }

  createUserWithCode(code: string, type: string): Observable<any> {
    let params = new HttpParams();

    if (code) {
      params = params.append('code', code);
    }

    const url = type === SignUpTypeEnum.LINKEDIN
      ? `${userUrl}/playground/linkedin`
      : `${userUrl}/playground/github`;

    return this.http.post<any>(url, {}, {headers, params});
  }


  getActivityUsers(): Observable<any> {
    return this.http.get<any>(`${userUrl}/admin`);
  }

  getUsersExport(data: {
    name: string;
    surname: string;
    userName: string;
    email:any;
    developerId: any;
    roles: any;
    companyRole: string;
    lastLoginDate: any;
    lastAccessDate: any;
    sessionDuration: any;
  }[]): Observable<HttpResponse<Blob>> {
    return this.http.post<Blob>(
      `${environment.apiBaseUrl}platform/export/users`,
      data,
      {
        responseType: 'blob' as 'json',
        observe: 'response'
      }
    );
  }
}
